/**
 * Single source of truth for responsive breakpoints.
 *
 * These values are also declared on the MUI theme (muiTheme.ts) so that `sx` breakpoint
 * helpers and `useMediaQuery(theme.breakpoints...)` agree with the raw CSS.
 *
 * CSS cannot read custom properties inside an `@media` condition, so `responsive.css`
 * repeats 600px and 1200px literally. Changing a value here means changing it there too;
 * the header comment in responsive.css points back at this file.
 */
export const BREAKPOINTS = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
} as const

export type LayoutMode = 'compact' | 'medium' | 'expanded'

/** Highest width still considered `compact`. */
export const COMPACT_MAX = BREAKPOINTS.sm - 0.05

/** Lowest width considered `expanded` (full desktop layout). */
export const EXPANDED_MIN = BREAKPOINTS.lg

/** Admin is blocked below this width. See RESPONSIVE-DESIGN-PLAN.md section 9. */
export const ADMIN_MIN_WIDTH = BREAKPOINTS.md

/**
 * Master switch for mobile/tablet layouts. Disabled following user feedback: every
 * viewport renders the desktop shell until this is flipped back to `true`.
 *
 * Turning it off does three things and nothing else:
 *   - `useComputedLayoutMode` reports `expanded` regardless of viewport width
 *   - `main.tsx` skips `responsive.css`, so no @media override applies
 *   - the viewport meta is pinned to DISABLED_LAYOUT_VIEWPORT_WIDTH so small screens
 *     scale the desktop layout down instead of laying out at device width
 *
 * All responsive code stays in place; re-enabling is a one-line change here.
 */
export const RESPONSIVE_LAYOUT_ENABLED: boolean = false

/** Layout width small screens are pinned to while responsive layouts are disabled. */
export const DISABLED_LAYOUT_VIEWPORT_WIDTH = 1280
